import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `admin.policies` in Brazilian Portuguese.
 *
 * The administrator plane, so the technical names stay technical (AGM-022, D-15): `Top-k`,
 * `TTL`, `wall clock` and `USD` are read in English by every Brazilian operator, and
 * translating them would make this form harder to use, not easier.
 *
 * Two word choices are deliberate and worth stating:
 *
 *  - *run* is **execução** everywhere in this product, so a *runtime profile* is a **perfil
 *    de execução** — the profile a run executes under. Not *perfil de runtime*.
 *  - an approval *round* is a **ciclo**, not a *rodada*. ADR-0018 bans *rodada* as the word
 *    for a run, and letting it back in here for a different idea is exactly how one word
 *    comes to mean two things.
 *  - a retrieval *minimum score* keeps **score**, the word a Brazilian search or ML engineer
 *    actually uses, and it is the same word in `admin.studio`'s resolve preview. One term per
 *    idea across both surfaces (LOC-060) matters more here than staying inside D-15's list,
 *    which names examples rather than a closed set.
 */
export const policies: Messages['admin']['policies'] = {
  title: 'Perfis de execução',
  lead: 'Roteamento de modelo, ajuste de recuperação, tetos de orçamento e limites de aprovação e delegação. Um agente roda sob exatamente um perfil; as variáveis de ambiente são apenas os valores iniciais do perfil embutido.',
  loading: 'Carregando os perfis…',
  unavailable: 'O plano de políticas ainda não está disponível nesta instalação',
  unavailableBody: 'GET /api/admin/policies não respondeu. A edição de perfis fica desativada até isso mudar.',
  empty: 'Nenhum perfil ainda',
  emptyBody: 'O perfil embutido `standard` é criado a partir dos valores de ambiente no primeiro boot.',

  builtin: 'embutido',
  assigned: (fmt, count) =>
    pluralCategory(fmt.locale, count) === 'one'
      ? `${fmt.formatCount(count)} agente`
      : `${fmt.formatCount(count)} agentes`,
  version: (version) => `v${version}`,
  edit: 'Editar',
  cancel: 'Cancelar',
  newProfile: 'Novo perfil',
  duplicate: 'Duplicar',

  rowSummary: (fmt, profile) =>
    `${profile.modelAlias} · ${profile.strategy} · top-k ${fmt.formatCount(profile.topK)} · máx. ${
      pluralCategory(fmt.locale, profile.maxSteps) === 'one'
        ? `${fmt.formatCount(profile.maxSteps)} etapa`
        : `${fmt.formatCount(profile.maxSteps)} etapas`
    } · ${fmt.formatCost(profile.maxCostUsd)}`,

  nameLabel: 'Nome',
  namePlaceholder: 'high-assurance',
  descriptionLabel: 'Descrição',
  descriptionPlaceholder: 'Para que serve este perfil e quais agentes devem rodar sob ele.',
  noteLabel: 'Nota da mudança (auditada)',
  notePlaceholder: 'Por que isto está mudando.',

  sections: {
    model: 'Roteamento de modelo',
    retrieval: 'Recuperação',
    budget: 'Tetos de orçamento',
    approval: 'Aprovação',
    delegation: 'Delegação'
  },

  fields: {
    modelAlias: 'Alias do modelo',
    strategy: 'Estratégia',
    topK: 'Top-k',
    minScore: 'Score mínimo',
    candidates: 'Candidatos',
    maxSteps: 'Máx. de etapas',
    maxModelCalls: 'Máx. de chamadas ao modelo',
    maxToolCalls: 'Máx. de chamadas de ferramenta',
    maxDelegations: 'Máx. de delegações',
    maxCostUsd: 'Custo máx. (USD)',
    maxWallClockMs: 'Máx. de wall clock (ms)',
    ttlMs: 'TTL da aprovação (ms)',
    maxRounds: 'Máx. de ciclos de aprovação',
    maxDepth: 'Profundidade máx.',
    maxParallel: 'Máx. em paralelo'
  },

  dryRun: 'Prever mudanças',
  dryRunning: 'Calculando o que muda…',
  dryRunTitle: 'O que esta edição mudaria',
  dryRunEmpty: 'Nenhum agente resolve de forma diferente depois desta edição.',
  dryRunAgents: (fmt, count) =>
    pluralCategory(fmt.locale, count) === 'one'
      ? `${fmt.formatCount(count)} agente afetado`
      : `${fmt.formatCount(count)} agentes afetados`,
  dryRunBefore: 'antes',
  dryRunAfter: 'depois',
  dryRunFailed: 'Não foi possível gerar a prévia. Nada foi gravado.',
  dryRunRequired:
    'Veja a prévia da mudança antes de salvar — um salvamento aqui alcança todos os agentes sob este perfil.',

  save: 'Salvar perfil',
  saving: 'Salvando…',
  saved: 'Salvo',
  saveFailed: 'Não foi possível salvar o perfil.',
  versionConflict: 'Outra pessoa salvou este perfil enquanto você editava. Recarregue antes de salvar de novo.',

  delete: 'Excluir perfil',
  deleting: 'Excluindo…',
  deleteConfirm: 'Excluir este perfil? Os agentes precisam ser reatribuídos antes.',
  deleteRefused: 'Este perfil ainda está em uso',
  deleteRefusedBody: 'Reatribua cada agente abaixo a outro perfil e então exclua este. Nada foi alterado.',
  deleteBuiltin: 'O perfil embutido não pode ser excluído.',
  deleteFailed: 'Não foi possível excluir o perfil.',

  assignmentsTitle: 'Atribuições',
  assignmentsLead: 'Um perfil por agente. Mudar uma atribuição passa a valer na próxima execução do agente.',
  assignmentsLoading: 'Carregando as atribuições…',
  assignmentsEmpty: 'Nenhum agente tem atribuição explícita — todos rodam sob o perfil embutido.',
  assignmentsUnavailable: 'As atribuições ainda não estão disponíveis nesta instalação.',
  agentColumn: 'Agente',
  profileColumn: 'Perfil',
  reassign: 'Reatribuir',
  reassigning: 'Reatribuindo…',
  reassignFailed: 'Não foi possível reatribuir esse agente.'
}
